/**
 * useToolbarResize – drag-to-resize hook for the floating toolbar (vertical).
 * Supports resize from top edge (up=grow) AND bottom edge (down=grow).
 * Pattern matches useMainNavResize.ts.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from '@xstate/store/react';
import { toolbarStore, TOOLBAR_STAGE_HEIGHTS, type ToolbarStage } from './toolbarStore';
import { setDragBodyStyles, stopDragBodyStyles } from '../../../lib/dragResizeUtils';

export function useToolbarResize() {
  const stage = useSelector(toolbarStore, (s) => s.context.stage);
  const rawHeight = useSelector(toolbarStore, (s) => s.context.rawHeight);
  const isDragging = useSelector(toolbarStore, (s) => s.context.isDragging);

  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const ownDragRef = useRef(false);
  // +1 = top (up grows), -1 = bottom (down grows)
  const directionRef = useRef<1 | -1>(1);

  const startResize = useCallback((e: React.MouseEvent, direction: 1 | -1) => {
    e.preventDefault();
    e.stopPropagation();
    ownDragRef.current = true;
    directionRef.current = direction;
    startYRef.current = e.clientY;
    startHeightRef.current = toolbarStore.getSnapshot().context.rawHeight;
    toolbarStore.send({ type: 'toolbar.startDrag' });
  }, []);

  const handleResizeStartTop = useCallback(
    (e: React.MouseEvent) => startResize(e, 1),
    [startResize],
  );

  const handleResizeStartBottom = useCallback(
    (e: React.MouseEvent) => startResize(e, -1),
    [startResize],
  );

  const handleResizeDoubleClick = useCallback(() => {
    const current = toolbarStore.getSnapshot().context.stage;
    const nextStage: ToolbarStage = current === 'tiny' ? 'medium' : 'tiny';
    toolbarStore.send({ type: 'toolbar.setStage', stage: nextStage });
  }, []);

  useEffect(() => {
    if (!isDragging || !ownDragRef.current) return;

    setDragBodyStyles('row-resize');

    const handleMouseMove = (e: MouseEvent) => {
      // top: up=grow → sign * (startY - clientY), sign=+1
      // bottom: down=grow → sign * (startY - clientY), sign=-1 → clientY - startY
      const deltaY = directionRef.current * (startYRef.current - e.clientY);
      const newHeight = Math.max(
        TOOLBAR_STAGE_HEIGHTS['tiny'],
        Math.min(TOOLBAR_STAGE_HEIGHTS['full'], startHeightRef.current + deltaY),
      );
      toolbarStore.send({ type: 'toolbar.setRawHeight', height: newHeight });
    };

    const handleMouseUp = () => {
      ownDragRef.current = false;
      toolbarStore.send({ type: 'toolbar.endDrag' });
      stopDragBodyStyles();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      stopDragBodyStyles();
    };
  }, [isDragging]);

  return {
    toolbarHeight: rawHeight,
    stage,
    isResizing: isDragging,
    resizeDirectionRef: directionRef,
    handleResizeStartTop,
    handleResizeStartBottom,
    handleResizeDoubleClick,
  };
}
